import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { useBrandingStore } from '../stores/brandingStore';
import { toApiError } from './errors';

/**
 * Refresh-token coordination — at most one in-flight refresh; concurrent 401s
 * await the same promise then retry their original request with the new token.
 */
type RefreshFn = (refreshToken: string) => Promise<{ accessToken: string; refreshToken?: string }>;

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Inject the standard headers every API call needs:
 *   - `Authorization: Bearer <accessToken>` (when authenticated)
 *   - `branding_key`  — current tenant branding key (drives backend resolution)
 *   - `x-tenant-id`   — current tenant id (regional API only; ignored by Core)
 */
function attachAuthHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const auth = useAuthStore.getState();
  const branding = useBrandingStore.getState();

  const apiKey = auth.apiKey;
  if (apiKey) {
    config.headers.set('X-API-Key', apiKey);
  } else {
    const token = auth.accessToken;
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const brandingKey = branding.getBrandingKey();
  if (brandingKey) {
    //config.headers.set('branding_key', brandingKey);
  }

  const tenantId = auth.tenantId;
  if (tenantId) {
    config.headers.set('x-tenant-id', tenantId);
  }

  return config;
}

interface RegisterRefreshOptions {
  /** Implementation of POST /auth/refresh — supplied by the api module that owns it. */
  refresh: RefreshFn;
  /** Called when refresh fails terminally — typically routes the user to login. */
  onRefreshFailed?: () => void;
}

/**
 * Wire the standard request + response interceptors onto an axios client.
 * Both the Core API and Tenant (regional) API clients call this — the only
 * difference between them is which `refresh` they pass in.
 */
export function installInterceptors(client: AxiosInstance, options: RegisterRefreshOptions): void {
  client.interceptors.request.use(attachAuthHeaders, (error) => Promise.reject(error));

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
      const status = error.response?.status;

      const isAuthEndpoint =
        original?.url?.includes('/auth/login') ||
        original?.url?.includes('/portal-auth/login') ||
        original?.url?.includes('/auth/refresh');

      if (
        status === 401 &&
        original &&
        !original._retried &&
        !isAuthEndpoint &&
        !useAuthStore.getState().isApiKeyAuth()
      ) {
        original._retried = true;

        const newToken = await coordinatedRefresh(options);
        if (newToken) {
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
          return client.request(original);
        }

        options.onRefreshFailed?.();
      }

      return Promise.reject(toApiError(error));
    },
  );
}

async function coordinatedRefresh(options: RegisterRefreshOptions): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;

  const auth = useAuthStore.getState();
  const refreshToken = auth.refreshToken;
  if (!refreshToken) return null;

  inflightRefresh = (async () => {
    try {
      const result = await options.refresh(refreshToken);
      const next = useAuthStore.getState();
      // Persist the new pair via the store's setAuth-equivalent path.
      // We fold this in here rather than calling setAuth so we don't require
      // a full AuthResponse — refresh endpoints typically return only tokens.
      const nextAccess = result.accessToken;
      const nextRefresh = result.refreshToken ?? next.refreshToken;
      // setAuth would require a User; use direct setters.
      useAuthStore.setState({ accessToken: nextAccess, refreshToken: nextRefresh });
      try { localStorage.setItem('paas_access_token', nextAccess); } catch { /* noop */ }
      const { secureStorage } = await import('../lib/secureStorage');
      if (nextRefresh) await secureStorage.set('paas_refresh_token', nextRefresh);
      return nextAccess;
    } catch (err) {
      console.warn('[interceptors] refresh failed:', err);
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}
